// Renders an individual assistant (Lema) chat message
import React from 'react';
import { Message, ChatFile, MessageCitation } from '../../types/chat';
import { MessageActions } from '../message-actions';
import { formatTimestamp } from '../../lib/chatUtils';
import { useChatContext } from '@/contexts/ChatContext';
import { BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AgentActivityIndicator } from './AgentActivityIndicator';
import { AgentStepsTimeline } from './AgentStepsTimeline';
import { usePathname } from 'next/navigation';
import { LinkPill } from './LinkPill';
import { IdeaCards } from './IdeaCards';
import { ErrorCard } from './ErrorCard';
import { InlineCardsRenderer } from './InlineCardsRenderer';
import { isRateLimitError } from '@/lib/errorUtils';
import { ERROR_CODES, WARNING_I18N_KEYS } from '@/lib/dify/errorUtils';
import { assistantMessageProfile, normalizeMessageContentWithProfile } from '@/lib/messageContent';
import { createMarkdownComponents, MarkdownRenderer } from './markdown/primitives';
import { assistantMessageMarkdownShell, assistantMessageMarkdownTable, assistantMessageMarkdownTypography } from './markdown/tokens';
import { ChatAttachmentList, ChatBubble, ChatBubbleHeader, ChatReplySnippet } from './message-primitives';
import { useNormalizedMessageCards } from '@/hooks/useNormalizedMessageCards';
import { renderTextWithCitations } from '@/lib/citations/renderTextWithCitations';
import { CitationList } from './citations/CitationList';
import { getPinnedTopCardIndices, removeHorizontalCardPlaceholders } from './messageCardPlacement';
import { isVisibleAgentActivity } from '@/lib/agent/activityFilters';
import { formatSourceSectionAsBlockquote } from '@/lib/sourceSectionFormatter';

interface LemaMessageProps {
  message: Message;
  files: ChatFile[];
  isLastAssistantMessage: boolean;
  scrollToMessage: (messageId: string) => void;
  onCitationClick?: (citation: MessageCitation) => void;
  onShowCitations?: (citations: MessageCitation[]) => void;
}

const NotifyPrompt: React.FC<{
  onNotifyMe: () => void;
  notifyArmed: boolean;
}> = ({ onNotifyMe, notifyArmed }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center space-x-3 px-3 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800">
      <span className="text-xs text-neutral-600 dark:text-neutral-400">
        {notifyArmed ? t('notification_set') : t('notify_when_done')}
      </span>
      {!notifyArmed && (
        <button
          onClick={onNotifyMe}
          className="text-xs px-2 py-1 rounded-md bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/20 transition-colors"
        >
          {t('notify_me')}
        </button>
      )}
    </div>
  );
};

export const LemaMessage: React.FC<LemaMessageProps> = ({
  message,
  files,
  isLastAssistantMessage,
  scrollToMessage,
  onCitationClick,
  onShowCitations,
}) => {
  const {
    activeChat,
    isResponding,
    handleRegenerate,
    sendFeedback,
    handleReplyToMessage,
    messageHoverStates,
    handleMessageHover,
  } = useChatContext();
  const { t } = useTranslation();
  const [copyStatus, setCopyStatus] = React.useState<'idle' | 'success' | 'error'>('idle');
  const [showNotifyPrompt, setShowNotifyPrompt] = React.useState(false);
  const [notifyArmed, setNotifyArmed] = React.useState(false);
  const notifyArmedRef = React.useRef(false); // ref survives re-renders for reliable effect reads
  const noop = React.useCallback(() => { }, []);
  const pathname = usePathname();

  const isHovering = messageHoverStates[message.id] || false;
  const isStreaming = isResponding && isLastAssistantMessage;
  const hasAgentActivity = Array.isArray(message.agentActivity) && message.agentActivity.length > 0;

  // Check if there are any tool call activities to show in timeline
  const hasToolCalls = React.useMemo(() => {
    if (!hasAgentActivity) return false;
    return message.agentActivity?.some(a => a.type === 'tool_call' || a.type === 'search') ?? false;
  }, [hasAgentActivity, message.agentActivity]);

  const showAgentActivityIndicator = (isStreaming || message.isThinking) && hasAgentActivity && !hasToolCalls;

  // Check if all activities would be filtered out (internal nodes, unlabeled, etc)
  const hasVisibleActivities = React.useMemo(() => {
    if (!hasAgentActivity) return false;
    return message.agentActivity?.some(isVisibleAgentActivity) ?? false;
  }, [hasAgentActivity, message.agentActivity]);

  // Retroactively detect rate limit errors in old messages
  const hasRateLimitError = React.useMemo(() => {
    // First check if the flag is already set
    if (message.isRateLimitError) return true;

    // Otherwise, check the content for error patterns
    if (message.content && typeof message.content === 'string') {
      // Check if content contains "Stream Error:" or "Error:" patterns
      const errorMatch = message.content.match(/\*?Stream Error:\s*(.+?)\*?/i) ||
        message.content.match(/^Error:\s*(.+)/im);
      if (errorMatch) {
        const errorText = errorMatch[1];
        return isRateLimitError(errorText);
      }
    }

    return false;
  }, [message.isRateLimitError, message.content]);

  // Detect Dify agent errors (iteration limits, tool failures)
  const hasDifyAgentError = React.useMemo(() => {
    return !!message.isDifyAgentError;
  }, [message.isDifyAgentError]);

  // Detect content policy errors
  const hasContentPolicyError = React.useMemo(() => {
    return !!message.isContentPolicyError;
  }, [message.isContentPolicyError]);

  // Check if this is a recovery failure (requires user action)
  const isRecoveryFailure = React.useMemo(() => {
    if (!hasDifyAgentError) return false;

    // Use errorCode for stable comparison across language changes
    if (message.errorCode) {
      return message.errorCode === ERROR_CODES.CHAT.COULD_NOT_RECOVER_RESPONSE;
    }

    // Fallback for backwards compatibility with old messages (pre-errorCode)
    return message.errorMessage === t(ERROR_CODES.CHAT.COULD_NOT_RECOVER_RESPONSE);
  }, [hasDifyAgentError, message.errorCode, message.errorMessage, t]);

  // Detect stream connection errors (TCP failures, network issues)
  const hasStreamError = React.useMemo(() => {
    return !!message.isStreamError && !isStreaming;
  }, [message.isStreamError, isStreaming]);

  // Detect incomplete responses (timeout)
  // Don't show if more specific errors are present (priority: streamError > contentPolicy > incomplete)
  const hasIncompleteResponse = React.useMemo(() => {
    return !!message.isIncomplete && !isStreaming && !hasStreamError && !hasContentPolicyError;
  }, [message.isIncomplete, isStreaming, hasStreamError, hasContentPolicyError]);


  const repliedToMessage = message.replyToMessageId && activeChat
    ? activeChat.messages.find((m: Message) => m.id === message.replyToMessageId)
    : undefined;

  const messageFiles = message.fileIds
    ?.map(fileId => files.find(f => f.id === fileId))
    .filter((file): file is ChatFile => !!file) || [];

  React.useEffect(() => {
    if (copyStatus !== 'idle') {
      const timer = setTimeout(() => setCopyStatus('idle'), 2000);
      return () => clearTimeout(timer);
    }
  }, [copyStatus]);

  const hasMessageContent = typeof message.content === 'string' && message.content.trim().length > 0;

  // Show notification prompt after 25s while the assistant is busy; hide it once done or content arrives.
  React.useEffect(() => {
    const isAssistantBusy = isStreaming || message.isThinking;
    if (isAssistantBusy && !hasMessageContent) {
      const timer = setTimeout(() => setShowNotifyPrompt(true), 25_000);
      return () => clearTimeout(timer);
    } else {
      setShowNotifyPrompt(false);
    }
  }, [isStreaming, message.isThinking, hasMessageContent]);

  // Keep notification prompt visible once activated until final content starts streaming.
  const showNotifyPromptActive = showNotifyPrompt;

  const fireNotification = React.useCallback(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Lema is done!', {
        body: 'Your response is ready.',
        icon: '/favicon.ico',
      });
    }
  }, []);

  // Fire notification when isStreaming transitions false — read armed state from ref to avoid stale closure / batching issues
  React.useEffect(() => {
    if (!isStreaming && notifyArmedRef.current) {
      notifyArmedRef.current = false;
      setNotifyArmed(false);
      fireNotification();
    }
  }, [isStreaming, fireNotification]);

  // Unmount fallback: if component unmounts while still armed (e.g. message object re-keyed on stream end), fire then
  React.useEffect(() => {
    return () => {
      if (notifyArmedRef.current) {
        notifyArmedRef.current = false;
        fireNotification();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNotifyMe = React.useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    const permission = Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();
    if (permission === 'granted') {
      notifyArmedRef.current = true;
      setNotifyArmed(true);
    }
  }, []);

  const onCopy = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(message.content);
        setCopyStatus('success');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = message.content;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (success) {
          setCopyStatus('success');
        } else {
          throw new Error('Fallback copy failed');
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
      setCopyStatus('error');
    }
  };

  const shouldShowIdeaCards = (pathname || '').startsWith('/chat');
  const { cards: ideaBlocks, remainder: remainderText } = useNormalizedMessageCards(message, {
    enabled: shouldShowIdeaCards,
  });


  const [hoveredCitation, setHoveredCitation] = React.useState<number | null>(null);

  // Pre-compute total citation counts from full message content
  const citationTotalCounts = React.useMemo(() => {
    const counts = new Map<number, number>();
    const regex = /\[(\d+)\]/g;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(message.content)) !== null) {
      const n = parseInt(m[1], 10);
      if (n > 0) counts.set(n, (counts.get(n) ?? 0) + 1);
    }
    return counts;
  }, [message.content]);

  // Fresh counter for each render — stable citation indices without mutation
  const citationCounterRef = React.useRef(new Map<number, number>());

  // Clear the counter at the start of each render to prevent stale accumulation
  citationCounterRef.current.clear();

  const handleCitationClick = React.useCallback((citationNumber: number) => {
    // Find the last badge for this citation number scoped to this message
    const prefix = `cite-${message.id}-${citationNumber}-`;
    let lastEl: Element | null = null;
    let lastIdx = -1;
    document.querySelectorAll(`[id^="${prefix}"]`).forEach((el) => {
      const idx = parseInt(el.id.slice(prefix.length), 10);
      if (idx > lastIdx) { lastIdx = idx; lastEl = el; }
    });
    if (lastEl) {
      (lastEl as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [message.id]);

  // Separate horizontal cards from other cards
  const horizontalCards = React.useMemo(
    () => ideaBlocks.filter(card => card.layout === 'horizontal'),
    [ideaBlocks]
  );
  const pinnedTopCardIndices = React.useMemo(
    () => getPinnedTopCardIndices(ideaBlocks),
    [ideaBlocks]
  );
  const pinnedTopCards = React.useMemo(
    () => pinnedTopCardIndices
      .map((index) => ideaBlocks[index])
      .filter((card): card is NonNullable<typeof card> => Boolean(card)),
    [ideaBlocks, pinnedTopCardIndices]
  );
  const extractedCards = React.useMemo(
    () => ideaBlocks.filter((card, index) => card.layout !== 'horizontal' && !pinnedTopCardIndices.includes(index)),
    [ideaBlocks, pinnedTopCardIndices]
  );
  const hasHorizontalCards = horizontalCards.length > 0;

  const stripPlanFromContent = React.useCallback((text: string): string => {
    if (!text) return text;

    // Don't strip content from error messages (including content policy errors)
    if (hasRateLimitError || hasDifyAgentError || hasContentPolicyError) return text;

    if (!Array.isArray(message.agentActivity) || message.agentActivity.length === 0) {
      return text;
    }

    const normalize = (value: unknown): string => {
      if (value == null) return '';
      if (typeof value === 'string') {
        return value.replace(/\s+/g, ' ').trim();
      }
      try {
        return JSON.stringify(value).replace(/\s+/g, ' ').trim();
      } catch {
        return String(value).replace(/\s+/g, ' ').trim();
      }
    };

    const planActivity = message.agentActivity
      .slice()
      .reverse()
      .find(a => {
        if (a.type !== 'thinking') return false;
        const rawThought = a.data?.thought;
        if (!rawThought) return false;
        const s = typeof rawThought === 'string' ? rawThought : (() => {
          try { return JSON.stringify(rawThought); } catch { return String(rawThought); }
        })();
        return s.trim().toLowerCase().startsWith('plan');
      });

    if (!planActivity) {
      return text;
    }

    const planRaw = planActivity.data?.thought;
    const normalizedPlan = normalize(planRaw);
    const normalizedText = normalize(text);

    // If the body is effectively the same as the plan text, omit it entirely.
    if (normalizedPlan.length > 0 && normalizedPlan === normalizedText) {
      return '';
    }

    // Backwards-compatible behaviour: if the first non-empty line is a "Plan:" label,
    // remove that label line and keep the rest.
    const lines = text.split(/\r?\n/);
    let firstIdx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].trim().length === 0) continue;
      firstIdx = i;
      break;
    }
    if (firstIdx === -1) return text;
    const first = lines[firstIdx].trim();
    if (!/^plan\s*:/i.test(first)) return text;
    lines.splice(firstIdx, 1);
    return lines.join('\n').replace(/^\s+/, '');
  }, [message.agentActivity, hasRateLimitError, hasDifyAgentError, hasContentPolicyError]);

  const contentToDisplay = shouldShowIdeaCards && ideaBlocks.length > 0 ? remainderText : message.content;
  const baseContent = contentToDisplay ?? message.content;

  const displayContent = React.useMemo(() => {
    return normalizeMessageContentWithProfile(
      baseContent,
      assistantMessageProfile({
        sanitizeStages: [stripPlanFromContent, formatSourceSectionAsBlockquote],
      })
    );
  }, [baseContent, stripPlanFromContent]);
  const inlineContent = React.useMemo(
    () => removeHorizontalCardPlaceholders(displayContent, pinnedTopCardIndices),
    [displayContent, pinnedTopCardIndices]
  );
  const hasInlinePlaceholders = inlineContent.includes('[HORIZONTAL_CARD:');
  const shouldRenderMessageBody = (inlineContent || '').trim().length > 0;

  const markdownComponents = React.useMemo(
    () => {
      return createMarkdownComponents({
        typography: assistantMessageMarkdownTypography,
        table: assistantMessageMarkdownTable,
        t,
        renderLink: (props) => (
          <LinkPill
            {...props}
            citations={message.citations || []}
            onCitationClick={onCitationClick}
            onCitationHover={(citation) => {
              if (onCitationClick) {
                onCitationClick(citation);
              }
            }}
          />
        ),
        renderTextWithCitations: (text) => renderTextWithCitations(text, {
          messageId: message.id,
          onCitationClick: handleCitationClick,
          hoveredCitation,
          onCitationHover: setHoveredCitation,
          totalCounts: citationTotalCounts,
          counterRef: citationCounterRef,
        }),
      });
    },
    [message.id, message.citations, onCitationClick, t, handleCitationClick, hoveredCitation, citationTotalCounts]
  );

  // Extracted message body renderer to eliminate duplication
  const renderMessageBody = React.useCallback(() => {
    if (!shouldRenderMessageBody) return null;

    // Use InlineCardsRenderer if there are placeholders, regardless of card layout
    // The renderer will handle filtering to horizontal cards internally
    if (hasInlinePlaceholders && ideaBlocks.length > 0) {
      return (
        <InlineCardsRenderer
          content={inlineContent}
          cards={ideaBlocks}
          markdownComponents={markdownComponents}
          shell={assistantMessageMarkdownShell}
        />
      );
    }

    return (
      <MarkdownRenderer
        content={inlineContent}
        components={markdownComponents}
        shell={assistantMessageMarkdownShell}
      />
    );
  }, [shouldRenderMessageBody, hasInlinePlaceholders, inlineContent, ideaBlocks, markdownComponents]);

  return (
    <ChatBubble
      messageId={message.id}
      align="start"
      bubbleClassName="bg-transparent text-neutral-800 dark:text-neutral-100"
      actionsClassName="absolute top-0 mt-1 right-0 translate-x-full ml-2 flex items-center space-x-1 z-10"
      onMouseEnter={() => handleMessageHover(message.id, true)}
      onMouseLeave={() => handleMessageHover(message.id, false)}
      header={(
        <ChatBubbleHeader
          icon={<img src="/images/lema-logo.png" alt="Lema" className="h-4 w-4 mr-2 flex-shrink-0" />}
          label="Lema"
          timestamp={formatTimestamp(message.timestamp)}
        />
      )}
      replySnippet={repliedToMessage ? (
        <ChatReplySnippet
          authorLabel={`${t('replying_to')} ${repliedToMessage.role === 'user' ? t('you') : 'Lema'}`}
          content={repliedToMessage.content}
          onClick={() => scrollToMessage(repliedToMessage.id)}
          title={t('jump_to_original')}
        />
      ) : undefined}
      body={(
        <>
          {showAgentActivityIndicator && hasVisibleActivities && (
            <div className="mb-2">
              {showNotifyPromptActive && (
                <div className="mb-2">
                  <NotifyPrompt onNotifyMe={handleNotifyMe} notifyArmed={notifyArmed} />
                </div>
              )}
              <AgentActivityIndicator activities={message.agentActivity ?? []} showPendingCard />
            </div>
          )}

          {(isStreaming || message.isThinking) && !hasMessageContent && (!hasAgentActivity || !hasVisibleActivities) && (
            <div className="mb-2">
              {showNotifyPromptActive ? (
                <NotifyPrompt onNotifyMe={handleNotifyMe} notifyArmed={notifyArmed} />
              ) : (
                <div className="rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-2 flex items-center space-x-2">
                  <span className="inline-flex h-4 w-4 rounded-full border-2 border-purple-400 border-t-transparent animate-spin" aria-hidden="true" />
                  <span className="text-xs text-neutral-700 dark:text-neutral-300">
                    {t('assistant_thinking')}
                  </span>
                </div>
              )}
            </div>
          )}

          {hasAgentActivity && !showAgentActivityIndicator && (
            <div className="mb-2">
              {showNotifyPromptActive && !hasMessageContent && (
                <div className="mb-2">
                  <NotifyPrompt onNotifyMe={handleNotifyMe} notifyArmed={notifyArmed} />
                </div>
              )}
              <AgentStepsTimeline
                activities={message.agentActivity ?? []}
                showDraftingCard={isStreaming && hasToolCalls}
              />
            </div>
          )}

          <div className="w-full min-w-0">
            {hasRateLimitError ? (
              <ErrorCard />
            ) : (
              <>
                <IdeaCards ideaBlocks={pinnedTopCards} />
                <IdeaCards ideaBlocks={extractedCards} />
                {renderMessageBody()}
                <CitationList citations={message.citations || []} t={t} messageId={message.id} />
                {hasContentPolicyError && (
                  <ErrorCard
                    title={t(ERROR_CODES.CHAT.CONTENT_POLICY_ERROR_TITLE)}
                    message={t(ERROR_CODES.CHAT.CONTENT_POLICY_ERROR_MESSAGE)}
                    variant="warning"
                  />
                )}
                {isRecoveryFailure && (
                  <ErrorCard
                    title={t(ERROR_CODES.CHAT.DIFY_AGENT_ERROR_TITLE)}
                    message={message.errorMessage}
                    variant="warning"
                  />
                )}
                {hasIncompleteResponse && (
                  <ErrorCard
                    title={t(WARNING_I18N_KEYS.CHAT.INCOMPLETE_RESPONSE_TITLE)}
                    message={t(WARNING_I18N_KEYS.CHAT.INCOMPLETE_RESPONSE)}
                    variant="warning"
                  />
                )}
                {hasStreamError && (
                  <ErrorCard
                    title={t(ERROR_CODES.CHAT.STREAM_ERROR_TITLE)}
                    message={t(ERROR_CODES.CHAT.STREAM_ERROR_MESSAGE)}
                    variant="warning"
                  />
                )}
              </>
            )}
          </div>
        </>
      )}
      attachments={(
        <ChatAttachmentList
          files={messageFiles}
          itemClassName="bg-white dark:bg-neutral-700"
          iconClassName="text-neutral-500 dark:text-neutral-400"
          textClassName="text-neutral-800 dark:text-neutral-200"
        />
      )}
      actions={isHovering ? (
        <>
          {message.hasCitations && onShowCitations && (
            <button
              type="button"
              onClick={() => onShowCitations(message.citations || [])}
              className="flex items-center justify-center h-8 w-8 rounded-full bg-neutral-700 text-neutral-200 hover:bg-neutral-600 transition-colors"
              title={t('show_sources', 'Show Sources')}
            >
              <BookOpen className="h-4 w-4" />
            </button>
          )}
          <MessageActions
            messageId={message.id}
            isAssistant={true}
            isLast={isLastAssistantMessage}
            isEditing={false}
            isHovering={isHovering}
            onCopy={onCopy}
            copyStatus={copyStatus}
            onEdit={noop}
            onReply={() => handleReplyToMessage(message)}
            onRegenerate={() => handleRegenerate(message.id)}
            showActions={!isResponding || isLastAssistantMessage}
            onLike={() => { void sendFeedback(message.id, 'like'); }}
            onDislike={() => { void sendFeedback(message.id, 'dislike'); }}
            feedback={message.feedback}
            isStreaming={isStreaming}
          />
        </>
      ) : undefined}
    />
  );
};
